import React, { useState, useEffect } from 'react';
import { Select, InputNumber, Tag, Space, Tooltip, Checkbox, Radio, Row, Col } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

// 定义项类型（金额统一为数组）
interface GiftCardPlansItem {
    id: number;
    name?: string;
    amount: number[]; // 金额数组（长度与 amountCount 一致）
    isDefault: boolean;
}

interface GiftCardPlansSelectProps {
    value?: string;
    onChange?: (value: string) => void;
    dataSource: any[]; // 每项需包含 id、name，可选 amountCount（默认1），可选 amountLabels（金额子标签数组）
    placeholder?: string;
    disabled?: boolean;
}

// ✅ 模块级常量：稳定的空数组引用，避免每次渲染新建数组导致无限循环
const EMPTY_ARRAY: any[] = [];

const GiftCardPlansSelect: React.FC<GiftCardPlansSelectProps> = ({
    value = '[]',
    onChange,
    dataSource = EMPTY_ARRAY,
    placeholder = '请选择扣款',
    disabled = false,
}) => {
    // 解析 JSON 字符串，金额统一转为数组
    const parseValue = (jsonStr: string): GiftCardPlansItem[] => {
        if (!jsonStr || typeof jsonStr !== 'string') return [];
        try {
            const parsed = JSON.parse(jsonStr);
            if (!Array.isArray(parsed)) return [];
            return parsed
                .filter((item: any) => item && item.id !== null && item.id !== undefined)
                .map((item: any) => {
                    // 处理 amount：可能是数字、数组或缺失
                    let amountArray: number[];
                    if (Array.isArray(item.amount)) {
                        amountArray = item.amount.map(Number);
                    } else if (typeof item.amount === 'number') {
                        amountArray = [item.amount];
                    } else {
                        amountArray = [0];
                    }
                    return {
                        id: Number(item.id),
                        name: item.name || undefined,
                        amount: amountArray,
                        isDefault: item.isDefault === true,
                    };
                });
        } catch (error) {
            console.warn('GiftCardPlansSelect: JSON 解析失败', error);
            return [];
        }
    };

    // 将内部状态序列化为 JSON 字符串，金额直接输出数组
    const formatValue = (items: GiftCardPlansItem[]): string => {
        if (!items.length) return '[]';
        const outputItems = items.map(item => {
            const typeInfo = dataSource.find(ds => Number(ds.id) === item.id);
            const amountCount = typeInfo?.amountCount ?? 1;
            // 确保输出的金额数组长度与 amountCount 一致，不足补0，多余截断
            const amountArray = Array.from({ length: amountCount }, (_, i) => item.amount[i] ?? 0);
            return {
                id: item.id,
                name: item.name,
                amount: amountArray,
                isDefault: item.isDefault,
            };
        });
        return JSON.stringify(outputItems);
    };

    // 获取某个选项的 amountCount（默认1）
    const getAmountCount = (id: number): number => {
        const dsItem = dataSource.find(item => Number(item.id) === id);
        return dsItem?.amountCount ?? 1;
    };

    // 获取某个选项的 amountLabels（金额子标签数组，若未定义则返回空数组）
    const getAmountLabels = (id: number): string[] => {
        const dsItem = dataSource.find(item => Number(item.id) === id);
        return dsItem?.amountLabels || [];
    };

    const initialItems = parseValue(value);

    const [selectedIds, setSelectedIds] = useState<number[]>(
        initialItems.map(item => item.id)
    );

    // 金额状态：每个 id 对应一个数组
    const [quantities, setQuantities] = useState<Record<number, number[]>>(() => {
        const qty: Record<number, number[]> = {};
        initialItems.forEach(item => {
            const count = getAmountCount(item.id);
            // 确保数组长度与 amountCount 一致，不足补0
            qty[item.id] = Array.from({ length: count }, (_, i) => item.amount[i] ?? 0);
        });
        return qty;
    });

    const [defaultFlags, setDefaultFlags] = useState<Record<number, boolean>>(() => {
        const flags: Record<number, boolean> = {};
        initialItems.forEach(item => {
            flags[item.id] = item.isDefault;
        });
        return flags;
    });

    const [names, setNames] = useState<Record<number, string | undefined>>(() => {
        const nameMap: Record<number, string | undefined> = {};
        initialItems.forEach(item => {
            nameMap[item.id] = item.name;
        });
        return nameMap;
    });

    // 监听 value 和 dataSource 变化，重新同步内部状态
    useEffect(() => {
        const items = parseValue(value);
        setSelectedIds(items.map(item => item.id));
        const newQuantities: Record<number, number[]> = {};
        const newDefaultFlags: Record<number, boolean> = {};
        const newNames: Record<number, string | undefined> = {};
        items.forEach(item => {
            const count = getAmountCount(item.id);
            newQuantities[item.id] = Array.from({ length: count }, (_, i) => item.amount[i] ?? 0);
            newDefaultFlags[item.id] = item.isDefault;
            newNames[item.id] = item.name;
        });
        setQuantities(newQuantities);
        setDefaultFlags(newDefaultFlags);
        setNames(newNames);
    }, [value, dataSource]); // 加入 dataSource 依赖，解决 dataSource 异步加载问题

    const getNameFromDataSource = (id: number): string | undefined => {
        const item = dataSource.find(item => Number(item.id) === id);
        return item?.name;
    };

    const getDisplayName = (id: number): string => {
        if (names[id] !== undefined) return names[id]!;
        const dsName = getNameFromDataSource(id);
        if (dsName) return dsName;
        return `未知扣款 (${id})`;
    };

    // 修改某个 id 的某个金额索引
    const handleAmountChange = (id: number, index: number, newAmount: number | null) => {
        if (newAmount === null || newAmount < 0) return;
        const count = getAmountCount(id);
        const currentArray = quantities[id] || [];
        const updatedArray = [...currentArray];
        // 扩展数组长度到 count，防止 dataSource 延迟加载时长度不足
        while (updatedArray.length < count) updatedArray.push(0);
        updatedArray[index] = newAmount;
        const newQuantities = { ...quantities, [id]: updatedArray };
        setQuantities(newQuantities);
        emitChange(selectedIds, newQuantities, defaultFlags, names);
    };

    // 选择/取消选择
    const handleSelectChange = (ids: number[]) => {
        const newQuantities = { ...quantities };
        const newDefaultFlags = { ...defaultFlags };
        const newNames = { ...names };

        ids.forEach(id => {
            if (!newQuantities[id]) {
                const count = getAmountCount(id);
                newQuantities[id] = new Array(count).fill(0);
            }
            if (newDefaultFlags[id] === undefined) {
                newDefaultFlags[id] = false;
            }
            if (newNames[id] === undefined) {
                const dsName = getNameFromDataSource(id);
                if (dsName) newNames[id] = dsName;
            }
        });

        Object.keys(newQuantities).forEach(idStr => {
            const id = Number(idStr);
            if (!ids.includes(id)) delete newQuantities[id];
        });
        Object.keys(newDefaultFlags).forEach(idStr => {
            const id = Number(idStr);
            if (!ids.includes(id)) delete newDefaultFlags[id];
        });
        Object.keys(newNames).forEach(idStr => {
            const id = Number(idStr);
            if (!ids.includes(id)) delete newNames[id];
        });

        setSelectedIds(ids);
        setQuantities(newQuantities);
        setDefaultFlags(newDefaultFlags);
        setNames(newNames);

        emitChange(ids, newQuantities, newDefaultFlags, newNames);
    };

    const handleDefaultChange = (id: number) => {
        const newDefaultFlags: Record<number, boolean> = {};
        selectedIds.forEach(itemId => {
            newDefaultFlags[itemId] = false;
        });
        newDefaultFlags[id] = true;
        setDefaultFlags(newDefaultFlags);
        emitChange(selectedIds, quantities, newDefaultFlags, names);
    };

    const handleDeselect = (removedId: number) => {
        const newIds = selectedIds.filter(id => id !== removedId);
        handleSelectChange(newIds);
    };

    const handleClear = () => {
        handleSelectChange([]);
    };

    const emitChange = (
        ids: number[],
        qtyMap: Record<number, number[]>,
        flagMap: Record<number, boolean>,
        nameMap: Record<number, string | undefined>
    ) => {
        if (!onChange) return;
        const items: GiftCardPlansItem[] = ids.map(id => ({
            id,
            name: nameMap[id],
            amount: qtyMap[id] || [],
            isDefault: flagMap[id] || false,
        }));
        onChange(formatValue(items));
    };

    const tagRender = (props: any) => {
        const { label, value: tagValue, closable, onClose } = props;
        const id = Number(tagValue);
        const amountCount = getAmountCount(id);
        const amountArray = quantities[id] || [];
        // 确保显示数组长度与 amountCount 一致，不足补0
        const displayAmounts = Array.from({ length: amountCount }, (_, i) => amountArray[i] ?? 0);
        const isDefault = defaultFlags[id] || false;
        const displayName = getDisplayName(id);
        const labels = getAmountLabels(id);

        let amountText: string;
        if (amountCount > 1) {
            // 组合扣款：显示每个子项的金额
            const parts = displayAmounts.map((amt, idx) => {
                const subLabel = labels[idx] || `金额${idx + 1}`;
                return `${subLabel}:${amt}`;
            });
            amountText = `(${parts.join(', ')})`;
        } else {
            amountText = displayAmounts[0]?.toString() ?? '0';
        }

        return (
            <Tag
                closable={closable && !disabled}
                onClose={onClose}
                style={{
                    marginRight: 8,
                    marginBottom: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    maxWidth: '100%',
                    borderColor: isDefault ? '#1890ff' : undefined
                }}
                color={isDefault ? 'blue' : undefined}
            >
                <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                    <span style={{ marginLeft: 4, color: '#666' }}> {amountText}</span>
                </span>
            </Tag>
        );
    };

    const selectedItems = selectedIds.map(id => ({
        id,
        name: getDisplayName(id),
        amountCount: getAmountCount(id),
        amountLabels: getAmountLabels(id),
        isDefault: defaultFlags[id] || false,
    }));

    return (
        <div>
            <Select
                mode="multiple"
                value={selectedIds.map(id => id.toString())}
                onChange={(ids: string[]) => handleSelectChange(ids.map(id => Number(id)))}
                onDeselect={(id: string) => handleDeselect(Number(id))}
                onClear={handleClear}
                tagRender={tagRender}
                placeholder={placeholder}
                disabled={disabled}
                allowClear
                style={{ width: '100%' }}
                // antd 6 中 suffixIcon 已废弃，但当前版本仍可用
                // 官方计划在 6.7.0 迁移到 suffix，届时可替换为 suffix={<Tooltip ...>...</Tooltip>}
                suffixIcon={
                    <Tooltip title="数据格式: [{'id':扣款ID,'name':'名称','amount':金额数组,'isDefault':是否默认}, ...]">
                        <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                }
            >
                {dataSource.map((item: any) => (
                    <Select.Option key={item.id} value={String(item.id)} disabled={disabled}>
                        {item.name || `ID: ${item.id}`}
                    </Select.Option>
                ))}
            </Select>

            {selectedItems.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <Row gutter={[12, 12]}>
                        {selectedItems.map((item) => {
                            // 根据 amountCount 动态生成显示金额数组，从 quantities 中取实际值，不足补0
                            const displayAmounts = Array.from(
                                { length: item.amountCount },
                                (_, i) => quantities[item.id]?.[i] ?? 0
                            );
                            return (
                                <Col key={item.id} span={8}>
                                    <div style={{
                                        border: '1px solid #f0f0f0',
                                        borderRadius: 4,
                                        padding: 12,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ fontWeight: 500, marginBottom: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {item.name}
                                        </div>
                                        {displayAmounts.map((amount, index) => {
                                            // 优先使用 amountLabels 中的标签，否则使用默认 “数值N”
                                            const label = item.amountLabels[index] || `数值${index + 1}`;
                                            return (
                                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                                                    <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                                                        {label}：
                                                    </span>
                                                    <InputNumber
                                                        min={0}
                                                        size="small"
                                                        value={amount}
                                                        onChange={(val) => handleAmountChange(item.id, index, val)}
                                                        style={{ width: 80 }}
                                                        disabled={disabled}
                                                        placeholder="金额"
                                                    />
                                                </div>
                                            );
                                        })}
                                        <div style={{ marginTop: 8 }}>
                                            <Tooltip title={item.isDefault ? "当前默认扣款" : "设为默认扣款"}>
                                                <Radio
                                                    checked={item.isDefault}
                                                    onChange={() => handleDefaultChange(item.id)}
                                                    disabled={disabled}
                                                >
                                                    {item.isDefault ? '默认' : '设默认'}
                                                </Radio>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}
        </div>
    );
};

export default GiftCardPlansSelect;